/**
 * Admin Clients — flat per-tenant list with cancellation + no-show
 * counters over a selectable window.
 *
 *   /admin/$slug/clients
 *
 * Available on every plan. The repeat-offender signal it surfaces is
 * the replacement for the old 24-hour cancellation cutoff — without
 * payment capture, after-the-fact analytics is the only real lever.
 *
 * Rows are server-sorted: most by-client cancellations first, then
 * no-shows, then name. Operators can immediately scan to the top to
 * see who's costing them.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Card } from '@bookaburra/ui';
import { fetchBookingTenant } from '../booking-flow/api';
import {
  fetchAdminClients,
  type AdminClientRow,
  type ClientsWindow,
} from '../booking-flow/admin-clients-api';
import { useDocumentTitle } from '../lib/use-document-title';

export const Route = createFileRoute('/admin/$slug/clients')({
  component: ClientsPage,
});

const WINDOW_OPTIONS: { value: ClientsWindow; label: string }[] = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 6 months' },
  { value: 365, label: 'Last 12 months' },
];

function ClientsPage() {
  const { slug } = Route.useParams();
  const [window, setWindow] = useState<ClientsWindow>(90);

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  const clientsQ = useQuery({
    queryKey: ['admin-clients', slug, window],
    queryFn: () => fetchAdminClients(slug, window),
  });

  useDocumentTitle(
    tenantQ.data
      ? `Clients · ${tenantQ.data.tenant.name} Admin`
      : 'Clients',
  );

  return (
    <main className="mx-auto max-w-6xl px-6 py-10 sm:px-12">
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">All clients</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Every client on your books, sorted to show repeat
              cancellers and no-shows first. Use this when you&rsquo;re
              deciding whether a client&rsquo;s behaviour is becoming a
              pattern.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setWindow(opt.value)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                window === opt.value
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-background text-muted-foreground hover:border-primary/40'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {clientsQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : clientsQ.error ? (
            <Alert variant="destructive">
              <AlertTitle>Couldn&rsquo;t load clients</AlertTitle>
              <AlertDescription>
                {(clientsQ.error as Error).message}
              </AlertDescription>
            </Alert>
          ) : (
            <ClientsTable
              clients={clientsQ.data?.clients ?? []}
              tenantTimezone={tenantQ.data?.tenant.timezone}
            />
          )}
        </div>
      </Card>
    </main>
  );
}

function ClientsTable({
  clients,
  tenantTimezone,
}: {
  clients: AdminClientRow[];
  tenantTimezone: string | undefined;
}) {
  if (clients.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
        No clients yet. Once someone books with you they&rsquo;ll
        appear here.
      </p>
    );
  }
  const tz = tenantTimezone ?? 'Australia/Sydney';
  const totalRepeatOffenders = clients.filter((c) => c.cancellationsByClient >= 2).length;

  return (
    <div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {clients.length} {clients.length === 1 ? 'client' : 'clients'}
        {totalRepeatOffenders > 0
          ? ` · ${totalRepeatOffenders} with 2+ cancellations`
          : ''}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Client</th>
              <th className="px-4 py-2 text-left">Contact</th>
              <th className="px-4 py-2 text-right">Total bookings</th>
              <th className="px-4 py-2 text-left">Last booking</th>
              <th className="px-4 py-2 text-right">Cancelled</th>
              <th className="px-4 py-2 text-right">No-shows</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {clients.map((c) => (
              <tr
                key={c.id}
                className={
                  c.cancellationsByClient >= 2 || c.noShows >= 1
                    ? 'bg-destructive/5'
                    : undefined
                }
              >
                <td className="px-4 py-2 font-medium">{c.name}</td>
                <td className="px-4 py-2 text-muted-foreground">
                  {c.email ? <span>{c.email}</span> : null}
                  {c.email && c.phone ? (
                    <span className="px-1.5 text-muted-foreground/60">·</span>
                  ) : null}
                  {c.phone ? <span>{c.phone}</span> : null}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {c.totalBookings}
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {c.lastBookingAt ? formatDate(c.lastBookingAt, tz) : '—'}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  <span
                    className={
                      c.cancellationsByClient >= 2
                        ? 'font-semibold text-destructive'
                        : undefined
                    }
                  >
                    {c.cancellationsByClient}
                  </span>
                  {c.cancellationsByStaff > 0 ? (
                    <span className="ml-1 text-xs text-muted-foreground">
                      (+{c.cancellationsByStaff} by you)
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  <span
                    className={
                      c.noShows >= 1
                        ? 'font-semibold text-destructive'
                        : undefined
                    }
                  >
                    {c.noShows}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatDate(epochMs: number, timezone: string): string {
  return new Date(epochMs).toLocaleDateString('en-AU', {
    timeZone: timezone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
